import { PhoneNumberUtil } from 'google-libphonenumber';
import moment, { Moment } from 'moment';

import {
  CompanyRegistrationRecord,
  RegistrationStep,
} from '@falcon/app/core/api/company-registration/company-registration.model';
import { DateTimeFormats } from '@falcon/app/shared/enums/datetime-formats.enum';

export enum CompanyRegistrationRoutePath {
  CONTACT = '',
  CONTACT_ADMIN = 'contact-admin',
  VERIFY_EMAIL = 'verify-email',
  SET_PASSWORD = 'set-password',
  IDENTITY = 'identity',
  STOPPED = 'stopped',
  REJECTED = 'rejected',
  UNDER_AGE = 'under-age',
  USER_DETAILS = 'user-details',
  OPEN_APP = 'open-app',
  ID_CARD = 'id-card',
  PC_CODE = 'pc-code',
  PC_CODE_NOT_SUPPORTED = 'pc-code-not-supported',
  PC_CODE_NOT_SURE = 'pc-code-not-sure',
  FUNCTIONS = 'functions',
  LEGAL_AGREEMENTS = 'legal-agreements',
  MANDATE = 'mandate',
  PENDING_ADMIN_VALIDATION = 'pending-admin-validation',
}

export const COMPANY_REGISTRATION_TOTAL_STEPS = 10;
export const COMPANY_REGISTRATION_STEP_LABELS: readonly string[] = [
  'Contact',
  'E-mail',
  'Wachtwoord',
  'Identiteit',
  'Sector',
  'Functie',
  'Voorwaarden',
  'Mandaat',
];
export const ONBOARDING_HELP_PHONE_NUMBER_INSTANCE =
  PhoneNumberUtil.getInstance().parse('+3292407611');

export enum CompanyRegistrationStep {
  CONTACT = 1,
  VERIFY_EMAIL = 2,
  SET_PASSWORD = 3,
  IDENTITY = 4,
  USER_DETAILS = 4,
  ID_CARD = 4,
  PC_CODE = 5,
  FUNCTIONS = 6,
  LEGAL_AGREEMENTS = 7,
  MANDATE = 8,
  PENDING_ADMIN_VALIDATION = 8,
}

export const MINIMUM_REPRESENTATIVE_AGE_YEARS = 18;

export function isOfMinimumAge(birthDate: Moment): boolean {
  return birthDate.isSameOrBefore(
    moment().subtract(MINIMUM_REPRESENTATIVE_AGE_YEARS, 'years'),
    'day'
  );
}

const ITSME_OUTCOME_STEPS: RegistrationStep[] = [
  'ITS_ME_VERIFICATION',
  'ITS_ME_VERIFICATION_QR_CODE',
  'PERSONAL_DATA_AUTO',
];

function isFlaggedAtItsmeOutcome(record: CompanyRegistrationRecord): boolean {
  const step = record.nextStep ?? record.currentStep;

  return (
    record.status === 'REQUIRE_MANUAL_VALIDATION' && !!step && ITSME_OUTCOME_STEPS.includes(step)
  );
}

export function isStoppedByUnderAge(record: CompanyRegistrationRecord): boolean {
  if (
    isFlaggedAtItsmeOutcome(record) &&
    record.identityVerified === true &&
    record.identityTrusted === false &&
    record.companyOwnerVerified !== false
  ) {
    return true;
  }

  const birthDate = record.owner?.birthDate;
  if (!birthDate) return false;

  const parsed = moment(birthDate, [DateTimeFormats.ISO_DATE, moment.ISO_8601], true);
  if (!parsed.isValid() || parsed.isAfter(moment(), 'day')) return false;

  return !isOfMinimumAge(parsed);
}

export function isStoppedAtRepresentativeCheck(record: CompanyRegistrationRecord): boolean {
  return (
    isFlaggedAtItsmeOutcome(record) &&
    record.companyOwnerVerified === false &&
    record.nextStep !== 'ITS_ME_VERIFICATION_QR_CODE'
  );
}

export const REGISTRATION_STEP_ROUTES: Record<RegistrationStep, CompanyRegistrationRoutePath> = {
  INITIAL: CompanyRegistrationRoutePath.CONTACT,
  EMAIL_VERIFICATION: CompanyRegistrationRoutePath.VERIFY_EMAIL,
  PASSWORD_CREATION: CompanyRegistrationRoutePath.SET_PASSWORD,
  ITS_ME_VERIFICATION: CompanyRegistrationRoutePath.IDENTITY,
  ITS_ME_VERIFICATION_QR_CODE: CompanyRegistrationRoutePath.IDENTITY,
  PERSONAL_DATA_AUTO: CompanyRegistrationRoutePath.IDENTITY,
  PERSONAL_DATA_MANUAL: CompanyRegistrationRoutePath.USER_DETAILS,
  IDENTITY_DOCUMENT: CompanyRegistrationRoutePath.ID_CARD,
  PC_CODE_SELECTION: CompanyRegistrationRoutePath.PC_CODE,
  FUNCTION_SELECTION: CompanyRegistrationRoutePath.FUNCTIONS,
  AGREEMENT_ACCEPTANCE: CompanyRegistrationRoutePath.LEGAL_AGREEMENTS,
  MANDATE_SETUP: CompanyRegistrationRoutePath.MANDATE,
  PENDING_ADMIN_VALIDATION: CompanyRegistrationRoutePath.PENDING_ADMIN_VALIDATION,
};

export const VERIFICATION_EXPIRED_STATE = 'verificationExpired';
